import { FlattenedPage } from "@types-runway/questionnaire";
import { User } from "@types-runway/user";
import {
  DependencyContext,
  getQuestionDependencyMatch,
  questionIsAlreadyAsked,
} from "@utils/questionnaire/validation";

export interface AskedScreensContext extends DependencyContext {
  user: User;
  areAddOnsEmpty: boolean;
}

// A dependsOn that cannot be evaluated yet needs an answer the patient has not given; it is not asked until it can be.
const dependencyMatches = (
  dependsOn: NonNullable<FlattenedPage["dependsOn"]>,
  context: AskedScreensContext
): boolean => {
  try {
    return getQuestionDependencyMatch(dependsOn, context);
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;

    return false;
  }
};

// Mirrors the applicability rules of the questionnaire page's shouldRender until ENG-743 shares them.
const isAsked = (page: FlattenedPage, context: AskedScreensContext): boolean => {
  if (page.inputType.input === "automatic") return false;
  if (page.stateKey === "addOns" && context.areAddOnsEmpty) return false;
  if (page.dependsOn) return dependencyMatches(page.dependsOn, context);
  if (page.storageMappings && context.isAuth) {
    return !questionIsAlreadyAsked(page.storageMappings.mappings, context.user);
  }

  return true;
};

export const selectAskedScreens = (
  flattenedPages: FlattenedPage[],
  context: AskedScreensContext
): FlattenedPage[] => flattenedPages.filter((page) => isAsked(page, context));

const hasAnswer = (context: AskedScreensContext, stateKey: string): boolean =>
  Boolean(context.questions?.[stateKey]?.answer);

// The screen whose answer a rule is still waiting on, or null when the rule can be decided.
// Conditionals that are not plain question rules are listed here; any other conditional is never undecided.
const awaitedStateKey = (
  dependsOn: NonNullable<FlattenedPage["dependsOn"]>,
  context: AskedScreensContext
): string | null => {
  if (dependsOn.type === "question") {
    const stateKey = dependsOn.question?.stateKey ?? "";

    return hasAnswer(context, stateKey) ? null : stateKey;
  }
  if (dependsOn.conditional === "syncStateOfResidence") {
    return hasAnswer(context, "stateOfResidence") ? null : "stateOfResidence";
  }
  if (dependsOn.conditional === "requiresBiometrics") {
    return context.dateOfBirth || hasAnswer(context, "dob") ? null : "dob";
  }

  return null;
};

// An undecided screen may still be asked, but only when the screen its answer comes from is counted.
const isUndecided = (
  page: FlattenedPage,
  countedScreens: FlattenedPage[],
  context: AskedScreensContext
): boolean => {
  if (!page.dependsOn || page.inputType.input === "automatic") return false;
  const awaitedKey = awaitedStateKey(page.dependsOn, context);
  if (!awaitedKey) return false;

  return countedScreens.some((screen) => screen.stateKey === awaitedKey);
};

// Walks the pages in order so a screen's source screen is already decided when the screen is reached.
export const selectCountedScreens = (
  flattenedPages: FlattenedPage[],
  context: AskedScreensContext
): FlattenedPage[] =>
  flattenedPages.reduce<FlattenedPage[]>(
    (countedScreens, page) =>
      isAsked(page, context) || isUndecided(page, countedScreens, context)
        ? [...countedScreens, page]
        : countedScreens,
    []
  );
